import {
  calculateRestaurantDeliveryFee,
  NoApplicableDeliveryTierError,
  type RestaurantDeliveryTier,
} from './restaurantDeliveryPricing';

export interface CartPricingLineItem {
  menuItemId: string;
  name: string;
  quantity: number;
  unitPriceMinorUnits: bigint;
  lineTotalMinorUnits: bigint;
}

export interface CartPickupOption {
  type: 'PICKUP';
  totalMinorUnits: bigint;
}

export interface CartDeliveryOption {
  type: 'RESTAURANT_DELIVERY';
  /**
   * null = subtotal is below the minimum order threshold for every configured tier.
   * The UI should show a "minimum order" notice rather than a total.
   */
  deliveryFeeMinorUnits: bigint | null;
  totalMinorUnits: bigint | null;
}

export interface CartPricing {
  lineItems: ReadonlyArray<CartPricingLineItem>;
  subtotalMinorUnits: bigint;
  /**
   * null = restaurant does not accept pickup (e.g. dark kitchens).
   */
  pickup: CartPickupOption | null;
  /**
   * null = this restaurant has no delivery tiers configured (delivery not offered).
   * Non-null with deliveryFeeMinorUnits=null = offered but subtotal is below the minimum order threshold.
   */
  delivery: CartDeliveryOption | null;
}

export interface ComputeCartPricingInput {
  items: ReadonlyArray<{
    menuItemId: string;
    name: string;
    quantity: number;
    unitPriceMinorUnits: bigint;
  }>;
  acceptsPickup: boolean;
  /** Pass RestaurantDeliveryTier[] from shared-domain. Pass [] when tiers are not yet loaded or restaurant does not offer delivery. */
  deliveryTiers: ReadonlyArray<RestaurantDeliveryTier>;
}

export function computeCartPricing(input: ComputeCartPricingInput): CartPricing {
  const lineItems: CartPricingLineItem[] = input.items.map((item) => ({
    menuItemId: item.menuItemId,
    name: item.name,
    quantity: item.quantity,
    unitPriceMinorUnits: item.unitPriceMinorUnits,
    lineTotalMinorUnits: item.unitPriceMinorUnits * BigInt(item.quantity),
  }));

  const subtotalMinorUnits = lineItems.reduce((acc, item) => acc + item.lineTotalMinorUnits, 0n);

  const pickup: CartPickupOption | null = input.acceptsPickup
    ? { type: 'PICKUP', totalMinorUnits: subtotalMinorUnits }
    : null;

  let delivery: CartDeliveryOption | null = null;
  if (input.deliveryTiers.length > 0) {
    try {
      const deliveryFeeMinorUnits = calculateRestaurantDeliveryFee(
        input.deliveryTiers,
        subtotalMinorUnits,
      );
      delivery = {
        type: 'RESTAURANT_DELIVERY',
        deliveryFeeMinorUnits,
        totalMinorUnits: subtotalMinorUnits + deliveryFeeMinorUnits,
      };
    } catch (error) {
      if (error instanceof NoApplicableDeliveryTierError) {
        delivery = {
          type: 'RESTAURANT_DELIVERY',
          deliveryFeeMinorUnits: null,
          totalMinorUnits: null,
        };
      } else {
        throw error;
      }
    }
  }

  return { lineItems, subtotalMinorUnits, pickup, delivery };
}
